import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Plus, MapPin, Globe, Trash2, ExternalLink, Pencil, FolderOpen, Copy, LogOut } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsDashboard({ token, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maps`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setProjects(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load projects', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleDelete = async (slug, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/maps/${slug}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setProjects(prev => prev.filter(p => p.slug !== slug));
      toast({ title: 'Deleted', description: `Project "${slug}" has been removed` });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleDuplicate = async (slug, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/maps/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const project = await res.json();
      const newSlug = `${slug}-copy`;
      const newBranding = { ...project.branding, organizationName: `${project.branding?.organizationName || 'Project'} (Copy)` };
      await fetch(`${API_URL}/api/maps`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ slug: newSlug, branding: newBranding, locations: project.locations }),
      });
      toast({ title: 'Duplicated', description: `Created "${newSlug}"` });
      fetchProjects();
    } catch {
      toast({ title: 'Error', description: 'Failed to duplicate', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="dashboard-title">My Projects</h1>
            <p className="text-sm text-slate-500 mt-1">Create and manage parking maps for your prospects</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/edit/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />New Project
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="text-slate-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-20 h-20 mx-auto mb-6 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No projects yet</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Create your first parking map to share with prospects. Add locations, customize branding, and publish a shareable link.
            </p>
            <Button
              onClick={() => navigate('/edit/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />Create First Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((project) => {
              const orgName = project.branding?.organizationName || 'Untitled Project';
              const primaryColor = project.branding?.primaryColor || '#3b82f6';
              const locationCount = project.locationCount ?? '—';
              const updatedAt = project.updatedAt ? new Date(project.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

              return (
                <Card
                  key={project.slug}
                  className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow border-slate-200"
                  onClick={() => navigate(`/edit/${project.slug}`)}
                  data-testid={`project-card-${project.slug}`}
                >
                  {/* Color bar */}
                  <div className="h-2" style={{ backgroundColor: primaryColor }} />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        {project.branding?.logoUrl ? (
                          <img src={project.branding.logoUrl} alt="" className="h-10 w-10 rounded-lg object-contain flex-shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}20` }}>
                            <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-800 truncate">{orgName}</h3>
                          <p className="text-xs text-slate-400 font-mono truncate">/view/{project.slug}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                      <span>{locationCount} location{locationCount !== 1 ? 's' : ''}</span>
                      <span>Updated {updatedAt}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline" size="sm"
                        className="flex-1 text-xs"
                        onClick={(e) => { e.stopPropagation(); navigate(`/edit/${project.slug}`); }}
                      >
                        <Pencil className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        className="flex-1 text-xs"
                        onClick={(e) => { e.stopPropagation(); window.open(`/view/${project.slug}`, '_blank'); }}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />View
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        className="text-xs"
                        onClick={(e) => handleDuplicate(project.slug, e)}
                        data-testid={`duplicate-${project.slug}`}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
                        onClick={(e) => handleDelete(project.slug, e)}
                        data-testid={`delete-${project.slug}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
